import { Download } from 'lucide-react'
import { useState } from 'react'
import type { RouterInputs, RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { Button } from 'shared/components/ui/button'
import { useTranslate } from 'shared/lib/i18n'
import { saveBase64File } from 'pages/cashdesk/ui/per-kg-file'

export type RemaindersExportInput = RouterInputs['remainders']['Export']
export type RemaindersExport = RouterOutputs['remainders']['Export']

const fileViaTrpc = (input: RemaindersExportInput) => trpcClient.remainders.Export.query(input)

const failure = (error: unknown) => (error instanceof Error ? error.message : String(error))

/**
 * «Скачать Excel» in the header of the remainders (ticket 47): the server builds the file from
 * the same read as the screen — exactly what this person sees, by their role — and
 * the browser saves it as it came (a Blob, as in «Килограммы»). The calls are
 * injected (`loadFile`, `saveFile`) so it is tested without a backend.
 */
export function RemaindersDownload({
	input,
	testId = 'remainders-download',
	loadFile = fileViaTrpc,
	saveFile = saveBase64File,
	disabled = false,
}: {
	input: RemaindersExportInput
	/** Nothing to download yet — no Point chosen. */
	disabled?: boolean
	testId?: string
	loadFile?: (input: RemaindersExportInput) => Promise<RemaindersExport>
	saveFile?: (file: RemaindersExport) => void
}) {
	const { t } = useTranslate()
	const [downloading, setDownloading] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const download = async () => {
		setDownloading(true)
		setError(null)
		try {
			saveFile(await loadFile(input))
		} catch (e) {
			setError(failure(e))
		} finally {
			setDownloading(false)
		}
	}

	return (
		<div className='flex items-center gap-3'>
			{error ? (
				<span role='alert' className='text-sm text-destructive' data-testid={`${testId}-error`}>
					{error}
				</span>
			) : null}
			<Button variant='outline' data-testid={testId} disabled={disabled || downloading} onClick={download}>
				<Download />
				{downloading ? t('remainders.downloading') : t('remainders.download')}
			</Button>
		</div>
	)
}
